// UsbOrdersPanel.jsx — v6.45.0 Sealed USB Orders fulfillment queue
// =================================================================
// Super Admin only. Lives inside SealedUsbHub.js and is the first
// panel operators see when they open the hub, because the queue is
// where every physical drive begins.
//
// Design intent (Tier-1 UI/UX):
//   * Sticky status strip at the top with click-to-filter chips.
//   * One row per order with a single, status-appropriate CTA on
//     the right — never ambiguous about what the operator should
//     click next.
//   * Slide-in drawer for details, keyboard-reachable, mobile-safe.
//   * Zero-retype at Authorize — buyer facts are frozen afterward.
//   * Every interactive element has a data-testid for QA.
//
// Backend endpoints:
//   GET   /api/usb-orders            (list, status filter, text search)
//   GET   /api/usb-orders/counts     (chip counts)
//   GET   /api/usb-orders/{id}       (full detail)
//   POST  /api/usb-orders            (manual entry)
//   POST  /api/usb-orders/{id}/authorize
//   POST  /api/usb-orders/{id}/hold
//   POST  /api/usb-orders/{id}/release
//   POST  /api/usb-orders/{id}/reject
//   POST  /api/usb-orders/{id}/qc-tick
//   POST  /api/usb-orders/{id}/qc-pass
//   POST  /api/usb-orders/{id}/ship
//   POST  /api/usb-orders/{id}/close
//   GET   /api/usb-orders/checklist
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import WorkflowStepper, { readMarks, writeMarks } from "./WorkflowStepper";
import { runResumeMintFlow } from "./usb/resumeMintFlow";
import { FinalQcReportSection } from "./FinalQcReportSection";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Palette: slate + amber retail aesthetic, matches the printed
//    USB packaging. No neon gradients, outlined status pills.
const STATUS_META = {
  incomplete:   { label: "Incomplete",      bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  new:          { label: "New",             bg: "#DBEAFE", fg: "#1E40AF", dot: "#2563EB" },
  authorized:   { label: "Authorized",      bg: "#E0E7FF", fg: "#3730A3", dot: "#4F46E5" },
  building:     { label: "Building",        bg: "#FEF3C7", fg: "#78350F", dot: "#D97706" },
  build_failed: { label: "Build Failed",    bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
  qc_pending:   { label: "QC Pending",      bg: "#F3E8FF", fg: "#6B21A8", dot: "#9333EA" },
  qc_passed:    { label: "QC Passed",       bg: "#DCFCE7", fg: "#166534", dot: "#16A34A" },
  qc_failed:    { label: "QC Failed",       bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
  shipped:      { label: "Shipped",         bg: "#D1FAE5", fg: "#064E3B", dot: "#059669" },
  closed:       { label: "Closed",          bg: "#E5E7EB", fg: "#374151", dot: "#6B7280" },
  on_hold:      { label: "On Hold",         bg: "#FEF3C7", fg: "#92400E", dot: "#F59E0B" },
  rejected:     { label: "Rejected",        bg: "#F3F4F6", fg: "#6B7280", dot: "#9CA3AF" },
};

const CHIP_ORDER = [
  "new", "incomplete", "authorized", "building", "qc_pending",
  "qc_passed", "shipped", "on_hold", "build_failed", "qc_failed",
  "closed", "rejected",
];

// Primary CTA label per status — one obvious next action per row.
const CTA_LABEL = {
  incomplete:   "Complete Details",
  new:          "Review & Authorize",
  authorized:   "Watch Build",
  building:     "Watch Build",
  build_failed: "View Errors",
  qc_pending:   "Open QC Checklist",
  qc_passed:    "Record Shipment",
  qc_failed:    "View Failures",
  shipped:      "Close Order",
  closed:       "View",
  on_hold:      "Review Hold",
  rejected:     "View",
};


// ─── Status Pill ─────────────────────────────────────────────────────
const StatusPill = ({ status, testid }) => {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span
      data-testid={testid || `status-pill-${status}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "3px 10px", borderRadius: 999, fontSize: 11,
        fontWeight: 700, letterSpacing: ".02em",
        background: "#FFFFFF", color: meta.fg,
        border: `1.5px solid ${meta.dot}`,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.dot }} />
      {meta.label.toUpperCase()}
    </span>
  );
};


// ─── Sticky Status Chip Strip ────────────────────────────────────────
const StatusChipStrip = ({ counts, active, onPick }) => (
  <div
    data-testid="usb-orders-chip-strip"
    style={{
      position: "sticky", top: 0, zIndex: 20,
      background: "#FFFFFFEE", backdropFilter: "blur(6px)",
      borderBottom: "1px solid #E2E8F0", padding: "10px 0",
      marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 8,
    }}
  >
    <button
      data-testid="chip-all"
      onClick={() => onPick(null)}
      style={{
        padding: "6px 12px", borderRadius: 999,
        border: !active ? "1.5px solid #0F172A" : "1px solid #CBD5E1",
        background: !active ? "#0F172A" : "#FFFFFF",
        color: !active ? "#FFFFFF" : "#334155",
        fontSize: 11, fontWeight: 700, cursor: "pointer",
      }}
    >
      ALL · {Object.values(counts || {}).reduce((a, b) => a + b, 0)}
    </button>
    {CHIP_ORDER.map((st) => {
      const n = counts?.[st] || 0;
      if (n === 0 && active !== st) return null;
      const meta = STATUS_META[st];
      const isActive = active === st;
      return (
        <button
          key={st}
          data-testid={`chip-${st}`}
          onClick={() => onPick(st)}
          style={{
            padding: "6px 12px", borderRadius: 999,
            border: isActive ? `2px solid ${meta.dot}` : `1px solid ${meta.dot}44`,
            background: isActive ? meta.bg : "#FFFFFF",
            color: meta.fg, fontSize: 11, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.dot }} />
          {meta.label.toUpperCase()} · {n}
        </button>
      );
    })}
  </div>
);


// ─── One Order Row ──────────────────────────────────────────────────
const OrderRow = ({ order, onOpen }) => {
  const meta = STATUS_META[order.status] || STATUS_META.new;
  const ago = useMemo(() => {
    try {
      const t = new Date(order.updated_at || order.created_at).getTime();
      const dt = Date.now() - t;
      if (dt < 60_000) return "just now";
      if (dt < 3_600_000) return `${Math.round(dt / 60_000)} min ago`;
      if (dt < 86_400_000) return `${Math.round(dt / 3_600_000)} hr ago`;
      return `${Math.round(dt / 86_400_000)} d ago`;
    } catch { return "—"; }
  }, [order.updated_at, order.created_at]);

  const usbLines = (order.line_items || []).filter(
    (li) => (li.sku || "").startsWith("MOS-USB-"),
  );
  const totalDrives = (order.drives || []).length ||
    usbLines.reduce((n, l) => n + (l.quantity || 1), 0);

  return (
    <div
      data-testid={`usb-order-row-${order.order_id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "6px 1fr auto",
        gap: 14,
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: 10,
        padding: 14,
        marginBottom: 10,
        boxShadow: "0 1px 2px rgba(15,23,42,.03)",
        overflow: "hidden",
      }}
    >
      <div style={{ background: meta.dot, borderRadius: 3 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <StatusPill status={order.status} testid={`row-pill-${order.order_id}`} />
          <span style={{ fontSize: 11, color: "#64748B" }}>{ago}</span>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>· {order.order_id}</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>
          {order.district_name}
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>
          {order.school_name}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11 }}>
          {usbLines.map((l, i) => (
            <span
              key={i}
              style={{
                padding: "2px 8px", background: "#F1F5F9", borderRadius: 4,
                color: "#334155", fontWeight: 600,
              }}
            >
              {l.sku} × {l.quantity || 1}
            </span>
          ))}
          {order.po_number && (
            <span style={{
              padding: "2px 8px", background: "#FFFBEB", borderRadius: 4,
              color: "#92400E", fontWeight: 600,
            }}>
              PO: {order.po_number}
            </span>
          )}
          {totalDrives > 0 && (
            <span style={{
              padding: "2px 8px", background: "#EEF2FF", borderRadius: 4,
              color: "#3730A3", fontWeight: 600,
            }}>
              {totalDrives} drive{totalDrives === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          data-testid={`row-cta-${order.order_id}`}
          onClick={() => onOpen(order.order_id)}
          style={{
            padding: "9px 16px",
            background: meta.dot,
            color: "#FFFFFF",
            border: "none",
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: ".01em",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {CTA_LABEL[order.status] || "View"} →
        </button>
      </div>
    </div>
  );
};


// ─── Authorize Modal ────────────────────────────────────────────────
const AuthorizeModal = ({ order, onClose, onAuthorized }) => {
  const [band, setBand] = useState(order.initial_grade_band || "6-8");
  const [qty, setQty] = useState(
    (order.line_items || []).find((l) => l.sku?.startsWith("MOS-USB-"))?.quantity || 1
  );
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [note, setNote] = useState("");
  const [pwStrength, setPwStrength] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Live password-strength probe (debounced).
  useEffect(() => {
    if (password.length < 4) { setPwStrength(null); return; }
    const t = setTimeout(() => {
      axios
        .post(`${API}/api/qc-usb/password/check`, { password })
        .then((r) => setPwStrength(r.data))
        .catch(() => setPwStrength(null));
    }, 250);
    return () => clearTimeout(t);
  }, [password]);

  // One-click: ask the backend for a policy-compliant strong password.
  const generatePw = async () => {
    try {
      const { data } = await axios.get(`${API}/api/qc-usb/password/suggest?length=16`);
      setPassword(data.password);
      setShowPw(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not generate a password");
    }
  };

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${order.order_id}/authorize`,
        {
          drives_per_band: { [band]: Number(qty) },
          password,
          strict: true,
          note: note || undefined,
        },
      );
      onAuthorized(data.order);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.detail || e.message || "Authorize failed"));
    } finally {
      setBusy(false);
    }
  };

  const strengthColor =
    !pwStrength ? "#CBD5E1" :
    pwStrength.ok ? "#16A34A" :
    pwStrength.strength_score > 2 ? "#F59E0B" : "#DC2626";

  return (
    <div
      data-testid="authorize-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#FFFFFF", borderRadius: 14, padding: 28, maxWidth: 560, width: "100%",
          boxShadow: "0 24px 48px rgba(15,23,42,.25)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20, color: "#0F172A" }}>
          Authorize & Build
        </h2>
        <p style={{ margin: "4px 0 20px", fontSize: 13, color: "#64748B" }}>
          {order.district_name} · {order.school_name}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: "#334155", fontWeight: 600 }}>
            Grade Band
            <select
              data-testid="authorize-band"
              value={band}
              onChange={(e) => setBand(e.target.value)}
              style={inputStyle}
            >
              {["K-2","K-5","3-5","6-8","9-12","K-8","K-12"].map((b) =>
                <option key={b} value={b}>{b}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: "#334155", fontWeight: 600 }}>
            # Drives
            <input
              data-testid="authorize-qty"
              type="number" min={1} max={50}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        <label style={{ fontSize: 12, color: "#334155", fontWeight: 600, display: "block", marginBottom: 14 }}>
          Shared Password (across all {qty} drive{qty > 1 ? "s" : ""})
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <input
              data-testid="authorize-password"
              type={showPw ? "text" : "password"}
              value={password}
              placeholder="12+ chars, 3 of 4 char classes"
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...inputStyle, marginTop: 0, flex: 1 }}
            />
            <button type="button" onClick={() => setShowPw((s) => !s)}
              data-testid="authorize-password-toggle"
              style={smallBtnStyle}>{showPw ? "Hide" : "Show"}</button>
            <button type="button" onClick={generatePw}
              data-testid="authorize-password-generate"
              style={smallBtnStyle}>Generate</button>
          </div>
          <div style={{ height: 4, background: "#E2E8F0", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: pwStrength ? `${Math.min(100, (pwStrength.strength_score || 0) * 25)}%` : "0%",
              background: strengthColor, transition: "width .2s, background .2s",
            }}/>
          </div>
          {pwStrength && (
            <div style={{ fontSize: 11, color: strengthColor, marginTop: 4 }}>
              {pwStrength.ok ? "✓ Meets policy" :
                `✗ ${(pwStrength.reasons || []).slice(0, 2).join(" · ")}`}
            </div>
          )}
        </label>

        <label style={{ fontSize: 12, color: "#334155", fontWeight: 600, display: "block", marginBottom: 20 }}>
          Note (optional, for audit trail)
          <textarea
            data-testid="authorize-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="e.g. Expedited build — buyer needs by Friday"
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </label>

        {err && (
          <div data-testid="authorize-error" style={{
            padding: 10, background: "#FEE2E2", color: "#991B1B",
            borderRadius: 6, fontSize: 12, marginBottom: 14,
          }}>{String(err)}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} data-testid="authorize-cancel" style={smallBtnStyle}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !password || !(pwStrength?.ok)}
            data-testid="authorize-submit"
            style={{
              ...smallBtnStyle,
              background: busy || !(pwStrength?.ok) ? "#CBD5E1" : "#16A34A",
              color: "#FFFFFF",
              borderColor: busy || !(pwStrength?.ok) ? "#CBD5E1" : "#16A34A",
              padding: "9px 18px",
            }}
          >
            {busy ? "Building…" : `Authorize ${qty} drive${qty > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Order Drawer (slide-in detail) ─────────────────────────────────
const OrderDrawer = ({ orderId, onClose, onChanged }) => {
  const [order, setOrder] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAuthorize, setShowAuthorize] = useState(false);
  const [trackingNo, setTrackingNo] = useState("");
  const [carrier, setCarrier] = useState("UPS Ground");
  const [actionErr, setActionErr] = useState(null);
  // v6.45.1 — Workflow stepper ops-confirmation marks (localStorage)
  const [marks, setMarks] = useState(() => readMarks(orderId));
  // v6.50 — Retry Mint control (R1-A, 2026-10-23). Replaces the owner-
  // rejected JavaScript bookmarklet for the resume-mint recovery path.
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryLog, setRetryLog] = useState([]);
  const [retrySummary, setRetrySummary] = useState(null);
  const updateMarks = useCallback((next) => {
    setMarks(next);
    writeMarks(orderId, next);
  }, [orderId]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/usb-orders/${orderId}`);
      setOrder(data.order);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    refresh();
    axios.get(`${API}/api/usb-orders/checklist`)
      .then((r) => setChecklist(r.data.checklist || []))
      .catch(() => {});
  }, [refresh]);

  // Poll while building so the operator sees drive progress.
  useEffect(() => {
    if (!order || !["building", "authorized"].includes(order.status)) return;
    const iv = setInterval(refresh, 4000);
    return () => clearInterval(iv);
  }, [order, refresh]);

  const tick = async (serial, checkId, ticked) => {
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/qc-tick`,
        { serial, check_id: checkId, ticked },
      );
      setOrder(data.order);
    } catch (e) {
      setActionErr(e?.response?.data?.detail || e.message || "Tick failed");
    }
  };

  const passQc = async (serial) => {
    setActionErr(null);
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/qc-pass`, { serial });
      setOrder(data.order);
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setActionErr(typeof d === "string" ? d : JSON.stringify(d));
    }
  };

  const ship = async () => {
    if (!trackingNo || trackingNo.length < 4) {
      setActionErr("Tracking number is required (4+ chars)"); return;
    }
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/ship`,
        { carrier, tracking_number: trackingNo },
      );
      setOrder(data.order);
      onChanged?.();
    } catch (e) {
      setActionErr(e?.response?.data?.detail || e.message || "Ship failed");
    }
  };

  const closeOrder = async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/close`,
        { reason: "Buyer confirmed receipt" },
      );
      setOrder(data.order);
      onChanged?.();
    } catch (e) {
      setActionErr(e?.response?.data?.detail || e.message || "Close failed");
    }
  };

  const rejectOrder = async () => {
    const reason = window.prompt("Why reject this order? (3+ chars, audit trail)", "");
    if (!reason || reason.length < 3) return;
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/reject`, { reason });
      setOrder(data.order);
      onChanged?.();
    } catch (e) {
      setActionErr(e?.response?.data?.detail || e.message || "Reject failed");
    }
  };

  const holdOrder = async () => {
    const reason = window.prompt("Reason for hold (3+ chars)", "");
    if (!reason || reason.length < 3) return;
    try {
      const { data } = await axios.post(
        `${API}/api/usb-orders/${orderId}/hold`, { reason });
      setOrder(data.order);
      onChanged?.();
    } catch (e) {
      setActionErr(e?.response?.data?.detail || e.message || "Hold failed");
    }
  };

  // v6.50 — Retry Mint (R1-A, 2026-10-23).
  // Replaces the owner-rejected bookmarklet procedure with an in-UI
  // action that:
  //   1. Opens Gate 3 with the backend minimum TTL (1h),
  //   2. Calls /resume-mint on THIS order only (serials preserved),
  //   3. Polls until status==="building" AND drives.length === 4 AND
  //      all 4 drives carry a mint_job_id — "dispatched" means jobs
  //      actually started, not just an order-status flip,
  //   4. Closes Gate 3 and verifies closure via /gate3/status.
  // Closure is reported CLOSED only when both the close returned ok
  // AND /status reports open=false + state="closed". Otherwise the
  // UI reports STILL_OPEN or UNKNOWN with an explicit warning naming
  // the TTL window — never silently "success".
  //
  // Control-flow is unit-tested in
  //   /app/frontend/src/components/usb/resumeMintFlow.test.mjs
  // (7 scenarios: happy path, dispatch HTTP error, timeout, close
  // without /status verification, bad /status shape, open-fails,
  // status=building without jobs).
  const retryEligible =
    order && (order.status === "gate3_blocked" || order.status === "building");

  const runRetryMint = async () => {
    if (!order) return;
    const confirmText = (
      `Retry mint for ${order.order_id}?\n\n` +
      `This will:\n` +
      `  1. Open Gate 3 (1h TTL — the backend minimum)\n` +
      `  2. Reset the ${order.drives?.length || 0} existing drive slots and ` +
      `re-dispatch mint — SERIALS AND LICENSES ARE PRESERVED\n` +
      `  3. Wait up to 60s to confirm the mint jobs attached\n` +
      `  4. Close Gate 3 and verify closure\n\n` +
      `No new order, serial, or license is created. ` +
      `Only this order is touched.`
    );
    if (!window.confirm(confirmText)) return;

    setRetryBusy(true);
    setRetryLog([]);
    setRetrySummary(null);
    setActionErr(null);

    const token = (() => {
      try { return localStorage.getItem("mosaic_session_token"); }
      catch (_) { return null; }
    })();

    const transport = async ({ method, path, body }) => {
      const resp = await fetch(`${API}/api${path}`, {
        method,
        credentials: "include",
        headers: {
          ...(body ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      let parsed = null;
      try { parsed = await resp.json(); } catch (_) {}
      return { status: resp.status, ok: resp.ok, body: parsed };
    };

    try {
      const summary = await runResumeMintFlow({
        orderId: order.order_id,
        transport,
        onProgress: ({ line }) => {
          setRetryLog((prev) => [...prev, line]);
        },
        config: {
          scope:
            `Retry mint for ${order.order_id} — Owner authorized ` +
            `(via Advanced Support)`,
        },
      });
      setRetrySummary(summary);
      // Refresh the order view so the drive statuses re-render.
      refresh();
      onChanged?.();
    } catch (e) {
      setActionErr(e?.message || "Retry mint failed unexpectedly");
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <div
      data-testid="order-drawer-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 90,
        display: "flex", justifyContent: "flex-end",
      }}
    >
      <div
        data-testid="order-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 100%)", height: "100%", background: "#F8FAFC",
          overflowY: "auto", borderLeft: "1px solid #E2E8F0",
          boxShadow: "-8px 0 24px rgba(15,23,42,.1)",
        }}
      >
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #E2E8F0", background: "#FFFFFF" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <StatusPill status={order?.status || "new"} />
            <button onClick={onClose} data-testid="drawer-close" style={smallBtnStyle}>Close ✕</button>
          </div>
          <h3 style={{ margin: "10px 0 0", fontSize: 17, color: "#0F172A" }}>
            {order?.district_name || "…"}
          </h3>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            {order?.school_name} · {order?.order_id}
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {loading && <div style={{ color: "#64748B" }}>Loading…</div>}
          {err && <div style={{ color: "#B91C1C" }}>{err}</div>}

          {order && (
            <>
              {/* v6.45.1 — Workflow stepper (fool-proof step-by-step) */}
              <WorkflowStepper
                order={order}
                marks={marks}
                onMarksChange={updateMarks}
                onAction={({ actionId }) => {
                  setActionErr(null);
                  if (actionId === "authorize") {
                    setShowAuthorize(true);
                  } else if (actionId === "focus-ship") {
                    document.querySelector('[data-testid="ship-tracking"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    document.querySelector('[data-testid="ship-tracking"]')?.focus();
                  } else if (actionId === "close") {
                    closeOrder();
                  }
                }}
              />

              {/* v6.50 — Retry Mint section (R1-A, 2026-10-23).
                  Visible only for orders in a resumable state. One
                  button; no owner developer-tools involvement. */}
              {retryEligible && (
                <SectionCard title="Mint Recovery">
                  <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
                    This order is stuck in <strong>{order.status}</strong>.
                    Use <strong>Retry Mint</strong> to re-dispatch the
                    existing {order.drives?.length || 0} serial(s) under
                    a brief, auto-closing Gate 3 window.
                    Serials, licenses, and prior R2 artifacts are preserved —
                    no new order or serial is created.
                  </div>
                  <button
                    data-testid="retry-mint-btn"
                    onClick={runRetryMint}
                    disabled={retryBusy}
                    style={{
                      padding: "9px 14px",
                      background: retryBusy ? "#CBD5E1" : "#B45309",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: retryBusy ? "wait" : "pointer",
                    }}
                  >
                    {retryBusy ? "Retry Mint running…" : "Retry Mint (preserves serials)"}
                  </button>

                  {retrySummary && (
                    <div
                      data-testid="retry-mint-summary"
                      style={{
                        marginTop: 14,
                        padding: 12,
                        borderRadius: 6,
                        background:
                          retrySummary.dispatchConfirmed && retrySummary.closeOutcome === "CLOSED"
                            ? "#DCFCE7"
                            : retrySummary.closeOutcome === "STILL_OPEN" || retrySummary.closeOutcome === "UNKNOWN"
                            ? "#FEF3C7"
                            : "#FEE2E2",
                        color: "#0F172A",
                        fontSize: 12,
                        lineHeight: 1.5,
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        {retrySummary.dispatchConfirmed && retrySummary.closeOutcome === "CLOSED"
                          ? "✅ Mint dispatched + Gate 3 CLOSED (verified)"
                          : retrySummary.dispatchConfirmed
                          ? `⚠️ Mint dispatched but Gate 3 closure: ${retrySummary.closeOutcome}`
                          : `❌ Mint NOT dispatched — Gate 3 closure: ${retrySummary.closeOutcome}`}
                      </div>
                      <div>Order: <code>{retrySummary.orderId}</code></div>
                      <div>
                        dispatchConfirmed: <strong>{String(retrySummary.dispatchConfirmed)}</strong>
                        {" · "}driveCount: <strong>{retrySummary.driveCount}</strong>
                        {" · "}dispatchedCount: <strong>{retrySummary.dispatchedCount}</strong>
                      </div>
                      {retrySummary.serials?.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          Serials (preserved): <code>{retrySummary.serials.join(", ")}</code>
                        </div>
                      )}
                      <div style={{ marginTop: 4 }}>
                        closeAttempted: <strong>{String(retrySummary.closeAttempted)}</strong>
                        {" · "}closeOutcome: <strong>{retrySummary.closeOutcome}</strong>
                      </div>
                      {(retrySummary.closeOutcome === "STILL_OPEN" ||
                        retrySummary.closeOutcome === "UNKNOWN") && (
                        <div style={{ marginTop: 6, color: "#B91C1C", fontWeight: 600 }}>
                          ⚠️ Gate 3 may remain OPEN until it auto-expires (1h TTL).
                          Use the Gate 3 strip at the top of the Fulfill Orders
                          board to close it manually and verify its state.
                        </div>
                      )}
                      {retrySummary.error && (
                        <div style={{ marginTop: 6, color: "#B91C1C" }}>
                          Error: {retrySummary.error}
                        </div>
                      )}
                      <div style={{ marginTop: 8, fontSize: 11, color: "#334155" }}>
                        Note: Dispatch confirmation means manufacturing is
                        <em> in flight</em>, not complete. Drives will transition
                        from <code>building</code> → <code>qc_pending</code>
                        (or <code>build_failed</code>) over the next minutes.
                      </div>
                    </div>
                  )}

                  {retryLog.length > 0 && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: "pointer", fontSize: 11, color: "#64748B" }}>
                        Show step-by-step log ({retryLog.length} lines)
                      </summary>
                      <pre
                        data-testid="retry-mint-log"
                        style={{
                          marginTop: 6, padding: 8, background: "#0F172A",
                          color: "#E2E8F0", borderRadius: 4, fontSize: 11,
                          maxHeight: 240, overflow: "auto",
                        }}
                      >
                        {retryLog.join("\n")}
                      </pre>
                    </details>
                  )}
                </SectionCard>
              )}

              {/* Buyer facts */}
              <SectionCard title={order.buyer_frozen ? "Buyer (Locked 🔒)" : "Buyer"}>
                <KV k="Contact" v={order.buyer_contact?.name || "—"} />
                <KV k="Email"   v={order.buyer_contact?.email || "—"} />
                <KV k="Phone"   v={order.buyer_contact?.phone || "—"} />
                <KV k="PO #"    v={order.po_number || "—"} />
                <KV k="Quote #" v={order.quote_number || "—"} />
                {order.shipping_address && (
                  <KV k="Ship To" v={
                    `${order.shipping_address.line1 || ""}, ${order.shipping_address.city || ""} ${order.shipping_address.state || ""} ${order.shipping_address.zip || ""}`.trim()
                  }/>
                )}
              </SectionCard>

              {/* Drives — only after Authorize */}
              {order.drives?.length > 0 && (
                <SectionCard title={`Drives (${order.drives.length})`}>
                  {order.shared_password_hint && (
                    <div style={{
                      marginBottom: 12, padding: 10, background: "#FEF3C7",
                      borderRadius: 6, fontSize: 11, color: "#78350F",
                    }}>
                      🔑 Shared password set · hint <strong>{order.shared_password_hint}</strong>
                      · never shown in full after Authorize. Keep the scratch-off label safe.
                    </div>
                  )}
                  {order.drives.map((d) => (
                    <DriveCard
                      key={d.serial}
                      drive={d}
                      orderId={order.order_id}
                      checklist={checklist}
                      canQc={["qc_pending", "qc_passed"].includes(d.status)}
                      onTick={tick}
                      onPassQc={passQc}
                      onReload={refresh}
                    />
                  ))}
                </SectionCard>
              )}

              {(order.drives || []).length > 0 && ["qc_pending", "qc_passed"].includes(order.status) && (
                <SectionCard title="Final Quality Report">
                  <FinalQcReportSection
                    orderId={order.order_id}
                    allPassed={(order.drives || []).every((d) => d.status === "qc_passed")}
                  />
                </SectionCard>
              )}

              {/* Shipping */}
              {["qc_passed", "shipped", "closed"].includes(order.status) && (
                <SectionCard title="Shipping">
                  {order.status === "qc_passed" ? (
                    <div data-testid="ship-form">
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
                        <select
                          data-testid="ship-carrier"
                          value={carrier}
                          onChange={(e) => setCarrier(e.target.value)}
                          style={inputStyle}
                        >
                          <option>UPS Ground</option>
                          <option>UPS 2-Day</option>
                          <option>FedEx Ground</option>
                          <option>USPS Priority</option>
                        </select>
                        <input
                          data-testid="ship-tracking"
                          value={trackingNo}
                          onChange={(e) => setTrackingNo(e.target.value)}
                          placeholder="Tracking number"
                          style={inputStyle}
                        />
                      </div>
                      <button
                        data-testid="ship-submit"
                        onClick={ship}
                        style={{ ...smallBtnStyle, marginTop: 10, background: "#059669", color: "#FFFFFF", borderColor: "#059669" }}
                      >
                        Mark Shipped →
                      </button>
                    </div>
                  ) : (
                    <>
                      <KV k="Carrier"      v={order.carrier || "—"} />
                      <KV k="Tracking #"   v={order.tracking_number || "—"} />
                      <KV k="Shipped At"   v={order.shipped_at || "—"} />
                    </>
                  )}
                </SectionCard>
              )}

              {/* v6.46 — Physical Replication panel. Surfaces the
                   one-image-many-sticks audit record stored on the
                   order (physical_replication) so a reader can see
                   at a glance "4 sticks hand-labeled Teacher 1-4"
                   without digging through the raw audit timeline. */}
              {order.physical_replication && (
                <SectionCard title="Physical Replication">
                  <div
                    data-testid="order-physical-replication"
                    style={{ fontSize: 12, color: '#334155' }}
                  >
                    <KV k="Mode"
                        v={order.physical_replication.mode === 'one_image_many_sticks'
                             ? 'One image · multiple physical sticks'
                             : order.physical_replication.mode} />
                    <KV k="Source Serial" v={order.physical_replication.source_serial || "—"} />
                    <KV k="Stick Count"   v={order.physical_replication.stick_count || "—"} />
                    <KV k="Recorded At"   v={order.physical_replication.recorded_at || "—"} />
                    <KV k="Recorded By"   v={order.physical_replication.recorded_by || "—"} />
                    {(order.physical_replication.labels || []).length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{
                          fontSize: 11, color: '#64748B',
                          textTransform: 'uppercase', letterSpacing: 0.6,
                          marginBottom: 4,
                        }}>
                          Hand-labels on each physical stick
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {order.physical_replication.labels.map((lbl, i) => (
                            <span
                              key={i}
                              data-testid={`physical-replication-label-${i}`}
                              style={{
                                fontSize: 11, fontWeight: 600,
                                background: '#DBEAFE', color: '#1E3A8A',
                                border: '1px solid #93C5FD',
                                padding: '3px 10px', borderRadius: 999,
                              }}
                            >
                              {lbl}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>
              )}

              {/* Timeline */}
              <SectionCard title={`Audit Timeline (${order.timeline?.length || 0})`}>
                <div data-testid="order-timeline" style={{ maxHeight: 220, overflowY: "auto" }}>
                  {(order.timeline || []).slice().reverse().map((t, i) => (
                    <div key={i} style={{
                      padding: "8px 10px", background: "#FFFFFF",
                      border: "1px solid #E2E8F0", borderRadius: 6,
                      marginBottom: 6, fontSize: 11, color: "#334155",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <strong style={{ color: "#0F172A" }}>{t.event}</strong>
                        <span style={{ color: "#94A3B8" }}>
                          {new Date(t.at).toLocaleString("en-US", {
                            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                          })}
                        </span>
                      </div>
                      {(t.from || t.to) && (
                        <div style={{ color: "#64748B" }}>
                          {t.from} → {t.to}
                        </div>
                      )}
                      {t.note && <div style={{ color: "#475569" }}>{t.note}</div>}
                      <div style={{ color: "#94A3B8", marginTop: 2, fontSize: 10 }}>by {t.user}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Action zone */}
              <SectionCard title="Actions">
                {actionErr && (
                  <div data-testid="action-error" style={{
                    padding: 10, background: "#FEE2E2", color: "#991B1B",
                    borderRadius: 6, fontSize: 12, marginBottom: 10,
                  }}>{String(actionErr)}</div>
                )}
                {order.status === "new" && (
                  <button
                    data-testid="action-authorize"
                    onClick={() => setShowAuthorize(true)}
                    style={{ ...smallBtnStyle, background: "#2563EB", color: "#FFFFFF", borderColor: "#2563EB", width: "100%", padding: "11px 18px" }}
                  >
                    🔐 Authorize & Build
                  </button>
                )}
                {order.status === "shipped" && (
                  <button
                    data-testid="action-close"
                    onClick={closeOrder}
                    style={{ ...smallBtnStyle, background: "#059669", color: "#FFFFFF", borderColor: "#059669", width: "100%", padding: "11px 18px" }}
                  >
                    ✓ Close Order (buyer confirmed receipt)
                  </button>
                )}
                {/* v6.46 — Print manifest: shipping / audit receipt
                    openable from any order drawer regardless of status. */}
                <a
                  href={`/admin/usbs/${orderId}/manifest`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="action-print-manifest"
                  style={{
                    ...smallBtnStyle, display: 'block', textAlign: 'center',
                    textDecoration: 'none', marginTop: 8, width: '100%',
                    background: '#F8FAFC', color: '#0F172A',
                  }}
                >
                  🧾 Print manifest
                </a>
                {/* v6.46 — Password Packing Slip: downloadable PDF with
                    the yellow DRIVE ACCESS PASSWORD callout — printed
                    and placed in the box with the physical USB(s).
                    Only shown once drives are minted (we need a
                    password in the vault to embed). */}
                {(order.drives || []).length > 0 && (
                  <button
                    data-testid="action-pin-packing-slip"
                    onClick={async () => {
                      try {
                        const tok = localStorage.getItem('mosaic_session_token');
                        const res = await axios.get(
                          `${API}/api/usb-orders/${orderId}/packing-slip.pdf`,
                          { responseType: 'blob', headers: { Authorization: `Bearer ${tok}` } },
                        );
                        const url = URL.createObjectURL(res.data);
                        window.open(url, '_blank', 'noopener');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                      } catch (e) {
                        let detail = e?.message || 'unknown error';
                        if (e?.response?.data instanceof Blob) {
                          try {
                            const t = await e.response.data.text();
                            detail = (JSON.parse(t).detail) || t;
                          } catch {}
                        }
                        alert(`Password packing slip failed: ${detail}`);
                      }
                    }}
                    style={{
                      ...smallBtnStyle, marginTop: 8, width: '100%',
                      background: '#FFFBEB', color: '#92400E',
                      borderColor: '#F59E0B', fontWeight: 700,
                    }}
                  >
                    🔑 Password Packing Slip (ships in box)
                  </button>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  {!["closed", "rejected"].includes(order.status) && (
                    <>
                      <button data-testid="action-hold" onClick={holdOrder} style={smallBtnStyle}>⏸ Hold</button>
                      <button data-testid="action-reject" onClick={rejectOrder} style={{ ...smallBtnStyle, color: "#DC2626", borderColor: "#FCA5A5" }}>✕ Reject</button>
                    </>
                  )}
                </div>
              </SectionCard>
            </>
          )}
        </div>

        {showAuthorize && order && (
          <AuthorizeModal
            order={order}
            onClose={() => setShowAuthorize(false)}
            onAuthorized={(o) => {
              setOrder(o);
              setShowAuthorize(false);
              onChanged?.();
            }}
          />
        )}
      </div>
    </div>
  );
};


// ─── Retail Prints — 4 polished PDFs for the retail box ────────────
// v6.45.5 — Surfaced in two places (drive card for anytime access, pack
// step card for when ops is actively packing). Served from
// /app/frontend/public/retail_box/ so no signed URL needed.
export const RETAIL_PRINTS = [
  { file: "MOSAIC_ThankYou_Letter.pdf",      icon: "💌", label: "Thank You Letter",       sub: "Welcome & gratitude · signed · page 1 of box" },
  { file: "MOSAIC_QuickStart_Retail.pdf",    icon: "🚀", label: "Quick Start Card",       sub: "One page · 4 numbered steps · cardstock" },
  { file: "MOSAIC_UserManual_Retail.pdf",    icon: "📘", label: "User Manual",            sub: "Multi-page · section-tabbed · booklet" },
  { file: "MOSAIC_PreInsert_Checklist.pdf",  icon: "☑️", label: "Pre-Insertion Checklist", sub: "One page · tick-box · 2-min pre-flight" },
];

const RetailPrintsPanel = ({ compact = false }) => (
  <div
    data-testid="retail-prints-panel"
    style={{
      marginTop: compact ? 10 : 12,
      padding: compact ? 10 : 12,
      background: compact ? "#FFFFFF" : "#FFFBEB",
      border: `1px solid ${compact ? "#E2E8F0" : "#FCD34D"}`,
      borderRadius: 8,
    }}
  >
    {!compact && (
      <div style={{
        fontSize: 11, fontWeight: 700, color: "#92400E",
        letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 8,
      }}>
        📄 Retail Prints · 4 polished PDFs for the box
      </div>
    )}
    {compact && (
      <div style={{
        fontSize: 11, fontWeight: 700, color: "#475569",
        marginBottom: 8,
      }}>
        📄 Retail Prints (click to download)
      </div>
    )}
    <div style={{ display: "grid", gap: 6 }}>
      {RETAIL_PRINTS.map((p) => (
        <a
          key={p.file}
          data-testid={`retail-print-${p.file.replace(/\.pdf$/, "").toLowerCase()}`}
          href={`/retail_box/${p.file}`}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 10px",
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: 6,
            textDecoration: "none",
            color: "#0F172A",
          }}
        >
          <span style={{ fontSize: 18 }}>{p.icon}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>
              {p.label}
            </div>
            <div style={{ fontSize: 10, color: "#64748B" }}>{p.sub}</div>
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#2563EB",
            padding: "4px 8px", background: "#DBEAFE", borderRadius: 4,
          }}>DOWNLOAD →</span>
        </a>
      ))}
    </div>
  </div>
);


// ─── DriveCard with QC Checklist ────────────────────────────────────
const DriveCard = ({ drive, orderId, checklist, canQc, onTick, onPassQc, onReload }) => {
  const ticked = drive.qc_checklist || {};
  const ok = checklist.length > 0 && checklist.every((c) => ticked[c.id]);
  const meta = STATUS_META[drive.status === "pending" ? "new" :
    drive.status === "qc_pending" ? "qc_pending" :
    drive.status === "qc_passed" ? "qc_passed" :
    drive.status === "building" ? "building" :
    drive.status === "build_failed" ? "build_failed" : "new"];

  // v6.45.7 / v6.45.9 — Download links for the minted artifacts.
  // Fetched on-demand. If the on-disk tarball is missing (ephemeral
  // pod storage was wiped by a redeploy), surface a Rebuild button
  // that re-runs the deterministic mint pipeline for this exact serial.
  const [links, setLinks] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksError, setLinksError] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const canDownload = ["qc_pending", "qc_passed", "packed", "shipped", "delivered"].includes(drive.status);
  const fetchLinks = async () => {
    if (loadingLinks || links) return;
    setLoadingLinks(true);
    setLinksError(null);
    try {
      const { data } = await axios.get(`${API}/api/qc-usb/${drive.serial}/mint-links`);
      setLinks(data.links);
    } catch (e) {
      setLinksError(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingLinks(false);
    }
  };
  const rebuildArtifacts = async () => {
    if (!window.confirm(
      `Re-mint ${drive.serial} to disk?\n\n` +
      `The build is deterministic — same inputs produce the same bytes — so this ` +
      `restores the .tar.gz / .sha256 / .img.gz artifacts that were wiped when the ` +
      `pod was redeployed. Takes ~90-120 seconds. You stay in QC_PENDING, no ` +
      `re-authorization needed.`
    )) return;
    setRebuilding(true);
    try {
      await axios.post(`${API}/api/usb-orders/${orderId}/drives/${drive.serial}/rebuild-artifacts`);
      alert(`Re-mint dispatched for ${drive.serial}. The drawer will show status 'building' then flip back to 'qc_pending' in about 90-120 seconds. Then click 'Generate download links' again.`);
      setLinks(null);
      setLinksError(null);
      if (onReload) onReload();
    } catch (e) {
      alert(`Rebuild failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div
      data-testid={`drive-card-${drive.serial}`}
      style={{
        background: "#FFFFFF", border: "1px solid #E2E8F0",
        borderRadius: 8, padding: 12, marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <strong style={{ fontSize: 13, color: "#0F172A", fontFamily: "monospace" }}>
          {drive.serial}
        </strong>
        <StatusPill status={
          drive.status === "pending" ? "new" :
          drive.status === "qc_pending" ? "qc_pending" :
          drive.status === "qc_passed" ? "qc_passed" :
          drive.status === "building" ? "building" :
          drive.status === "build_failed" ? "build_failed" : "new"
        } testid={`drive-pill-${drive.serial}`} />
      </div>
      {drive.sha256 && (
        <div style={{ fontSize: 10, color: "#64748B", fontFamily: "monospace", marginBottom: 6 }}>
          SHA-256: {drive.sha256.slice(0, 24)}…
        </div>
      )}
      {drive.error && (
        <div style={{
          padding: 8, background: "#FEE2E2", color: "#991B1B",
          borderRadius: 4, fontSize: 11, marginBottom: 8,
        }}>⚠ {drive.error}</div>
      )}

      {/* v6.45.7 — Download panel: available any time the drive has been built */}
      {canDownload && (
        <div
          data-testid={`drive-downloads-${drive.serial}`}
          style={{
            marginTop: 8, marginBottom: 8, padding: 10,
            background: "#F0F9FF", border: "1px solid #38BDF8",
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0C4A6E", marginBottom: 6 }}>
            ⬇️ Download artifacts to flash this drive
          </div>
          {!links ? (
            <>
              <button
                type="button"
                data-testid={`drive-generate-links-${drive.serial}`}
                onClick={fetchLinks}
                disabled={loadingLinks || rebuilding}
                style={{
                  padding: "8px 14px",
                  background: "#0284C7", color: "#FFFFFF",
                  border: "none", borderRadius: 6,
                  fontSize: 12, fontWeight: 600,
                  cursor: (loadingLinks || rebuilding) ? "wait" : "pointer",
                  width: "100%",
                }}
              >
                {loadingLinks ? "Generating signed URLs…" : "🔗 Generate 30-day download links"}
              </button>
              {linksError && (
                <div
                  data-testid={`drive-links-error-${drive.serial}`}
                  style={{
                    marginTop: 10, padding: 10,
                    background: "#FEF3C7", border: "1px solid #F59E0B",
                    borderRadius: 6, fontSize: 11, color: "#78350F",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    ⚠ {linksError}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    This drive's DB record says it was built, but its on-disk
                    artifacts were wiped (ephemeral pod storage dies on every
                    redeploy). The build is <b>deterministic</b> — same inputs
                    produce byte-identical output — so re-minting restores the
                    exact same files. No re-authorization needed, you stay in
                    QC_PENDING.
                  </div>
                  <button
                    type="button"
                    data-testid={`drive-rebuild-${drive.serial}`}
                    onClick={rebuildArtifacts}
                    disabled={rebuilding}
                    style={{
                      padding: "8px 14px",
                      background: "#B45309", color: "#FFFFFF",
                      border: "none", borderRadius: 6,
                      fontSize: 12, fontWeight: 700,
                      cursor: rebuilding ? "wait" : "pointer",
                      width: "100%",
                    }}
                  >
                    {rebuilding ? "Dispatching re-mint…" : "🔁 Rebuild artifacts (90–120s)"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11 }}>
              <a
                data-testid={`drive-dl-tarball-${drive.serial}`}
                href={`${API}${links.tarball}`}
                target="_blank" rel="noreferrer"
                download={`${drive.serial}.tar.gz`}
                style={dlLinkStyle}
              >
                📦 <code style={{ background:"transparent" }}>{drive.serial}.tar.gz</code>&nbsp;— the full USB image (verify SHA before flashing)
              </a>
              <a
                data-testid={`drive-dl-sha-${drive.serial}`}
                href={`${API}${links.sha256}`}
                target="_blank" rel="noreferrer"
                download={`${drive.serial}.tar.gz.sha256.txt`}
                style={dlLinkStyle}
              >
                🔐 <code style={{ background:"transparent" }}>{drive.serial}.tar.gz.sha256.txt</code>&nbsp;— SHA-256 sidecar
              </a>
              <a
                data-testid={`drive-dl-flashable-${drive.serial}`}
                href={`${API}${links.flashable_img}`}
                target="_blank" rel="noreferrer"
                download={`${drive.serial}.img.gz`}
                style={dlLinkStyle}
              >
                💾 <code style={{ background:"transparent" }}>{drive.serial}.img.gz</code>&nbsp;— flashable image (Balena Etcher / dd)
              </a>
              <a
                data-testid={`drive-dl-retail-${drive.serial}`}
                href={`${API}${links.retail_insert}`}
                target="_blank" rel="noreferrer"
                download={`${drive.serial}_retail_insert.pdf`}
                style={dlLinkStyle}
              >
                📄 Retail insert PDF (print, fold, drop into the box)
              </a>
              <div style={{ marginTop: 6, fontSize: 10, color: "#475569" }}>
                Links expire in 30 days. Rotating MONGO_URL invalidates them immediately.
              </div>
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: "#475569", lineHeight: 1.5 }}>
            <strong>How to flash:</strong> On the ops Mac, insert a formatted exFAT USB stick, then run:
            <pre style={{
              margin: "4px 0 0", padding: 6, background: "#0F172A",
              color: "#E2E8F0", borderRadius: 4, fontSize: 10, overflow: "auto",
            }}>{`# verify
curl -s "${links?.sha256 ? API + links.sha256 : '<SHA_URL>'}" | shasum -a 256 -c -

# flash via tarball (preserves file structure)
curl -L "${links?.tarball ? API + links.tarball : '<TARBALL_URL>'}" | tar -xzf - -C /Volumes/USB_NAME`}</pre>
          </div>
        </div>
      )}

      {canQc && (
        <div>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 700, marginBottom: 6, marginTop: 6 }}>
            Physical QC — all 10 ticks required
          </div>
          {checklist.map((c) => (
            <label
              key={c.id}
              data-testid={`qc-tick-${drive.serial}-${c.id}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "6px 0", cursor: "pointer", fontSize: 12,
              }}
            >
              <input
                type="checkbox"
                checked={!!ticked[c.id]}
                onChange={(e) => onTick(drive.serial, c.id, e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span style={{ color: ticked[c.id] ? "#64748B" : "#0F172A",
                              textDecoration: ticked[c.id] ? "line-through" : "none" }}>
                {c.label}
              </span>
            </label>
          ))}
          {ok && drive.status === "qc_pending" && (
            <button
              data-testid={`drive-qc-pass-${drive.serial}`}
              onClick={() => onPassQc(drive.serial)}
              style={{
                ...smallBtnStyle, marginTop: 10, width: "100%",
                background: "#16A34A", color: "#FFFFFF", borderColor: "#16A34A",
              }}
            >
              ✓ Mark Drive QC-Passed
            </button>
          )}
          {/* v6.45.5 — Retail prints always visible on each drive so
              ops can reprint any insert without leaving the drawer. */}
          <RetailPrintsPanel compact />
        </div>
      )}
    </div>
  );
};

const dlLinkStyle = {
  display: "block",
  padding: "6px 8px",
  background: "#FFFFFF",
  border: "1px solid #BAE6FD",
  borderRadius: 4,
  color: "#0C4A6E",
  textDecoration: "none",
  fontSize: 11,
  fontWeight: 600,
};


// ─── Helpers ────────────────────────────────────────────────────────
const SectionCard = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{
      fontSize: 11, color: "#64748B", fontWeight: 700, letterSpacing: ".04em",
      textTransform: "uppercase", marginBottom: 8,
    }}>{title}</div>
    <div style={{
      background: "#FFFFFF", border: "1px solid #E2E8F0",
      borderRadius: 8, padding: 12,
    }}>{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 10, padding: "4px 0", fontSize: 12 }}>
    <span style={{ color: "#94A3B8" }}>{k}</span>
    <span style={{ color: "#0F172A", wordBreak: "break-word" }}>{v}</span>
  </div>
);

const inputStyle = {
  display: "block", width: "100%", padding: "8px 10px", marginTop: 4,
  border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13,
  background: "#FFFFFF", color: "#0F172A",
  fontWeight: 400,
};

const smallBtnStyle = {
  padding: "7px 13px", background: "#FFFFFF", border: "1px solid #CBD5E1",
  borderRadius: 6, fontSize: 12, fontWeight: 600, color: "#334155", cursor: "pointer",
};


// ─── TOP-LEVEL PANEL ────────────────────────────────────────────────
const UsbOrdersPanel = ({ initialOrderId = null } = {}) => {
  // Honor URL hash so links like /admin/sealed-usb#incomplete auto-filter
  // the queue to that status (used by the dashboard quick-stats tiles).
  const _initialFilter = (() => {
    try {
      const h = (window.location.hash || "").replace(/^#/, "").trim().toLowerCase();
      const allowed = ["new","incomplete","authorized","building","qc_pending",
                       "qc_passed","packed","shipped","delivered","build_failed","rejected"];
      return allowed.includes(h) ? h : null;
    } catch { return null; }
  })();
  const [orders, setOrders]   = useState([]);
  const [counts, setCounts]   = useState({});
  const [filter, setFilter]   = useState(_initialFilter);
  const [query, setQuery]     = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [openId, setOpenId]   = useState(initialOrderId);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [listResp, countsResp] = await Promise.all([
        axios.get(`${API}/api/usb-orders`, { params: { status: filter || undefined, q: query || undefined } }),
        axios.get(`${API}/api/usb-orders/counts`),
      ]);
      setOrders(listResp.data.orders || []);
      setCounts(countsResp.data.counts || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load USB orders");
    } finally {
      setLoading(false);
    }
  }, [filter, query]);

  useEffect(() => { reload(); }, [reload]);

  // Light polling so new auto-created orders surface without a refresh.
  useEffect(() => {
    const iv = setInterval(reload, 15000);
    return () => clearInterval(iv);
  }, [reload]);

  return (
    <div data-testid="usb-orders-panel"
         style={{
           background: "#FFFFFF", border: "1px solid #E2E8F0",
           borderRadius: 12, padding: "18px 20px", marginBottom: 18,
           boxShadow: "0 1px 2px rgba(15,23,42,.03)",
         }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17, color: "#0F172A" }}>
            🛒 USB Orders — Fulfillment Queue
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748B" }}>
            Paid orders flow in automatically from accepted quotes <b>and</b> from any order created in the Orders folder with a MOS-USB-* SKU. Click Review to authorize & launch the build.
          </p>
        </div>
        <input
          data-testid="usb-orders-search"
          placeholder="Search district, PO, order ID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...inputStyle, width: 260, marginTop: 0 }}
        />
        <button
          type="button"
          data-testid="usb-orders-backfill-btn"
          onClick={async () => {
            if (!window.confirm("Scan Orders folder for any order containing MOS-USB-* SKUs and auto-create a USB fulfillment entry for each? Safe to run repeatedly.")) return;
            try {
              const { data } = await axios.post(`${API}/api/usb-orders/-/backfill-from-orders`);
              alert(`Backfill complete.\n\nCreated: ${data.created_count}\nAlready in queue: ${data.skipped_existing}`);
              // Reload list
              window.location.reload();
            } catch (e) {
              alert(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
            }
          }}
          style={{
            marginLeft: 8,
            padding: "8px 12px",
            background: "#F1F5F9",
            border: "1px solid #CBD5E1",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            color: "#0F172A",
            cursor: "pointer",
          }}
          title="Scan Orders folder for USB SKUs and backfill any missing fulfillment entries"
        >
          ↺ Backfill from Orders
        </button>
        <button
          type="button"
          data-testid="usb-orders-repair-incomplete-btn"
          onClick={async () => {
            if (!window.confirm("Promote any INCOMPLETE orders that have buyer name + shipping address to NEW?\n\n(PO number is captured later at the Authorize step — a missing PO alone no longer blocks NEW.)")) return;
            try {
              const { data } = await axios.post(`${API}/api/usb-orders/-/repair-incomplete`);
              const names = (data.repaired || []).map(r => r.school_name || r.order_id).join(", ") || "(none)";
              alert(`Repair complete.\n\nPromoted ${data.repaired_count} order(s) → NEW:\n${names}`);
              window.location.reload();
            } catch (e) {
              alert(`Repair failed: ${e?.response?.data?.detail || e.message}`);
            }
          }}
          style={{
            marginLeft: 8,
            padding: "8px 12px",
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            color: "#78350F",
            cursor: "pointer",
          }}
          title="Promote INCOMPLETE orders (with buyer + address but no PO) → NEW"
        >
          ⚡ Repair Incomplete
        </button>
        <button
          type="button"
          data-testid="usb-orders-prepare-platform-btn"
          onClick={async () => {
            if (!window.confirm("Prepare the production platform for USB builds?\n\nThis runs migration 005 (tags all questions with their WIDA standard) and reports the filesystem audio bundle size. Idempotent — safe to re-run.")) return;
            try {
              const { data } = await axios.post(`${API}/api/usb-orders/-/prepare-platform`);
              const lines = (data.steps || []).map(s => {
                const icon = s.status === "ok" ? "✅" : s.status === "warning" ? "⚠️" : "❌";
                return `${icon} ${s.step}\n   ${s.note || s.error || `tagged=${s.tagged}/${s.total}`}`;
              }).join("\n\n");
              alert(`Platform prep ${data.ok ? "complete" : "finished with issues"}:\n\n${lines}`);
            } catch (e) {
              alert(`Prep failed: ${e?.response?.data?.detail || e.message}`);
            }
          }}
          style={{
            marginLeft: 8,
            padding: "8px 12px",
            background: "#DBEAFE",
            border: "1px solid #3B82F6",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            color: "#1E3A8A",
            cursor: "pointer",
          }}
          title="Run migration 005 + audit filesystem audio bundle so preflight passes"
        >
          🔧 Prepare Platform
        </button>
      </div>

      <StatusChipStrip counts={counts} active={filter} onPick={setFilter} />

      {loading && !orders.length && (
        <div data-testid="usb-orders-loading" style={{ padding: 20, textAlign: "center", color: "#64748B" }}>
          Loading orders…
        </div>
      )}
      {err && (
        <div data-testid="usb-orders-error"
             style={{ padding: 12, background: "#FEE2E2", color: "#991B1B", borderRadius: 6, fontSize: 12 }}>
          {String(err)}
        </div>
      )}
      {!loading && !err && orders.length === 0 && (
        <div data-testid="usb-orders-empty" style={{ padding: 30, textAlign: "center", color: "#64748B" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
          No orders here yet. When a customer accepts a quote with any
          <code style={{ background: "#F1F5F9", padding: "0 6px", borderRadius: 4, margin: "0 4px" }}>MOS-USB-*</code>
          line item, it will land in this queue automatically.
        </div>
      )}
      {orders.map((o) => (
        <OrderRow key={o.order_id} order={o} onOpen={(id) => setOpenId(id)} />
      ))}

      {openId && (
        <OrderDrawer
          orderId={openId}
          onClose={() => { setOpenId(null); reload(); }}
          onChanged={reload}
        />
      )}
    </div>
  );
};

export default UsbOrdersPanel;
